import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { notifySuccess, notifyError, type ToastConfig } from '../../services/notify';

// Wraps useMutation with the common pattern of invalidating one query key after the mutation
// settles (success or error). Use directly when the only post-mutation work is invalidation;
// fall back to useMutation when you need optimistic updates, setQueryData, or multiple invalidations.
export function useInvalidatingMutation<TData, TVars = void>(
  queryKey: QueryKey,
  mutationFn: (vars: TVars) => Promise<TData>
) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

// useInvalidatingMutation + automatic success/error toasts. The toast builders receive the
// mutation result/vars (or the error/vars), so dynamic text — names, roles, counts — is built
// from whatever the caller passes through as vars. Pass display-only fields (e.g. fullName)
// in vars to keep the toast text in the hook rather than the component.
export function useToastMutation<TData, TVars = void>(
  queryKey: QueryKey,
  mutationFn: (vars: TVars) => Promise<TData>,
  toasts?: {
    success?: (data: TData, vars: TVars) => ToastConfig;
    error?: (error: Error, vars: TVars) => ToastConfig;
  }
) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVars>({
    mutationFn,
    // A custom error builder replaces the global default toast — suppress it to avoid double-toasting.
    meta: toasts?.error ? { suppressErrorToast: true } : undefined,
    onSuccess: toasts?.success ? (data, vars) => notifySuccess(toasts.success!(data, vars)) : undefined,
    onError: toasts?.error ? (error, vars) => notifyError(toasts.error!(error, vars)) : undefined,
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
